// Simulateur de paiement (développement et présentation seulement) : page du simulateur lue par
// GET /public/simulateur-paiement/:reference, puis actions confirmer, refuser, expirer ou rejouer
// qui produisent une notification signée traitée par le même chemin qu'un vrai prestataire. Quand
// le serveur n'expose pas le simulateur (404), l'écran le dit sans rien simuler lui-même.
import { Bandeau, Bouton, EtatVide, Squelette } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { Ban, CheckCircle2, Clock, Repeat } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { LIBELLES_ETAT_PAIEMENT, LIBELLES_TRAITEMENT, francs, libelle, type PageSimulateur } from './donnees';
import { INVALIDER_RECETTES } from './requetes';
import styles from './recettes.module.css';

interface ResultatSimulation {
  reference: string;
  action: string;
  traitement: string;
  etat: string;
  detail: string | null;
}

export const ACTIONS_SIMULATEUR = [
  { action: 'confirmer', libelle: 'Confirmer le paiement', icone: CheckCircle2, variante: 'principal' as const },
  { action: 'refuser', libelle: 'Refuser', icone: Ban, variante: 'secondaire' as const },
  { action: 'expirer', libelle: 'Expirer', icone: Clock, variante: 'secondaire' as const },
  { action: 'rejouer', libelle: 'Rejouer la dernière notification', icone: Repeat, variante: 'discret' as const },
];

export function Simulateur({ reference }: { reference: string }) {
  const page = useQuery({
    queryKey: ['recettes', 'simulateur', reference],
    queryFn: ({ signal }) => requete<PageSimulateur>(`/public/simulateur-paiement/${reference}`, { signal }),
    retry: false,
  });
  const [resultat, setResultat] = useState<ResultatSimulation | null>(null);
  const action = useAction<string, ResultatSimulation>({
    executer: (a) => requete<ResultatSimulation>(`/public/simulateur-paiement/${reference}/${a}`, { methode: 'POST', corps: {} }),
    invalider: [...INVALIDER_RECETTES, ['recettes', 'simulateur', reference]],
    apres: (r) => setResultat(r),
  });
  if (page.isPending) return <Squelette lignes={3} libelle="Lecture du simulateur" />;
  if (page.error) {
    const absent = page.error instanceof ErreurApi && page.error.statut === 404;
    return (
      <EtatVide titre={absent ? 'Simulateur de paiement indisponible' : 'Simulateur injoignable'}>
        <p>{absent ? 'Ce serveur n\'expose pas le simulateur (il est absent en production). La confirmation viendra du prestataire configuré.' : page.error instanceof Error ? page.error.message : 'Erreur inattendue'}</p>
      </EtatVide>
    );
  }
  const p = page.data;
  return (
    <section className={styles.simulateur} aria-label="Simulateur de paiement">
      <Bandeau type="attention">{p.mention}</Bandeau>
      <dl className={styles.segments}>
        <div>
          <dt>Référence</dt>
          <dd className={styles.mono}>{p.reference}</dd>
        </div>
        <div>
          <dt>Montant</dt>
          <dd className={styles.grand}>{francs(p.montant)}</dd>
        </div>
        <div>
          <dt>Bénéficiaire</dt>
          <dd>{p.beneficiaire}</dd>
        </div>
        <div>
          <dt>État</dt>
          <dd>{libelle(LIBELLES_ETAT_PAIEMENT, p.etat)}</dd>
        </div>
      </dl>
      <div className={styles.actions} role="group" aria-label="Actions du simulateur">
        {ACTIONS_SIMULATEUR.filter((a) => p.actions.includes(a.action)).map((a) => (
          <Bouton key={a.action} variante={a.variante} icone={a.icone} petit chargement={action.enCours} onClick={() => action.lancer(a.action)}>
            {a.libelle}
          </Bouton>
        ))}
      </div>
      {resultat ? (
        <p role="status">
          Notification {resultat.action === 'rejouer' ? 'rejouée' : 'envoyée'} : traitement <strong>{libelle(LIBELLES_TRAITEMENT, resultat.traitement)}</strong>, paiement {libelle(LIBELLES_ETAT_PAIEMENT, resultat.etat).toLowerCase()}
          {resultat.detail ? ` (${resultat.detail})` : ''}.
        </p>
      ) : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </section>
  );
}
